import React from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import styled from 'styled-components/macro';

import Preloader from '../Preloader';
import Header from './Header';
import ModalContent from './Content';
import Footer from './Footer';
import Icon from '../Input/Icon';

const ModalWrapper = styled.div<{ isMovable: boolean }>`
  z-index: ${({ theme }) => theme.zIndexPalette.high};
  position: relative;
  padding: 20px;
  background: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey3 : theme.colorPalette.white};
  border-radius: 5px;
  max-width: 90vw;
  max-height: calc(100vh - 40px);
  min-width: 300px;

  .handleDrag {
    height: 30px;
    background: transparent;
    margin: -15px -22px;
    position: relative;
    z-index: ${({ theme }) => theme.zIndexPalette.low};
    &:hover {
      cursor: ${(props) => (props.isMovable ? 'move' : 'default')};
    }
  }
`;

const CloseModalButton = styled.button`
  position: absolute;
  padding: 2px;
  top: 20px;
  right: 20px;
  z-index: ${({ theme }) => theme.zIndexPalette.medium};
  + button {
    right: 35px;
  }
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active,
  &:focus {
    outline: 0;
  }
`;

const TopLevelWrapper = styled.div<{ isVisible: boolean }>`
  display: ${(props: any) => (props.isVisible || props.visible ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndexPalette.high};
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

export interface Props {
  children: React.ReactElement;
  open: boolean;
  onClose?: Function;
  style?: object;
  isDraggable?: boolean;
  isClosable?: boolean;
  closingOnOverlayClick?: boolean;
  id?: string;
}

const Content = (props: Props) => {
  const {
    style,
    isDraggable = true,
    children,
    isClosable = true,
    onClose,
    open = false,
    closingOnOverlayClick = false,
    id,
  } = props;
  const draggableKey = open ? `modal-open-${Date.now()}` : 'modal-closed';
  const Inner = (
    <ModalWrapper id={id} isMovable={true} style={style} data-walkthrough={id}>
      <Preloader area="modal" />
      {isDraggable ? <div className="handleDrag">&nbsp;</div> : ''}
      {children}
      {isClosable && (
        <CloseModalButton
          onClick={() => {
            onClose && onClose();
          }}
          title="Close"
        >
          <Icon icon="times" size="lg" style={{ color: 'grey' }} />
        </CloseModalButton>
      )}
    </ModalWrapper>
  );

  return (
    <TopLevelWrapper isVisible={open}>
      <Overlay onClick={(e) => (closingOnOverlayClick ? onClose && isClosable && onClose() : e.stopPropagation())} />
      {props.isDraggable ? (
        <Draggable handle=".handleDrag" key={draggableKey}>
          {Inner}
        </Draggable>
      ) : (
        Inner
      )}
    </TopLevelWrapper>
  );
};

const Modal = (props: Props) => createPortal(<Content {...props} />, document.body);

Modal.Header = Header;
Modal.Content = ModalContent;
Modal.Footer = Footer;

export default Modal;
export { ModalContent as Content, Header, Footer };
